import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookmarkPlus, Download, Loader2, Lock, Sparkles, Trash2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'components/ui/button';
import { SegmentedTabs } from 'components/ui/data-table';
import Skeleton from 'components/ui/skeleton';
import EmptyState from 'components/ui/EmptyState';
import { useToast } from 'components/ui/use-toast';
import { PlanCheckoutModal } from 'components/portal/PlanCheckoutModal';
import {
  VOCAB_QUERY_KEY,
  defineVocabWords,
  deleteVocabWord,
  downloadVocabCsv,
  saveVocabWord,
  useVocabulary,
  vocabErrorCode,
  type VocabWord,
} from 'modules/vocabulary';
import { WordLists, type ListWord } from 'modules/wordLists';
import { PortalHeading } from './PortalHeading';

type WordsView = 'mine' | 'lists';

function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function WordRow({ item, locked, onDelete }: { item: VocabWord; locked: boolean; onDelete: () => void }) {
  const { t, i18n } = useTranslation();
  const def = item.definition;
  const translation = def ? (i18n.language.startsWith('ru') ? def.ru : def.uz) : '';
  return (
    <li className="flex gap-3 px-4 py-3.5 md:px-5">
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-display text-[17px] font-semibold text-foreground">{item.word}</span>
          {def?.partOfSpeech && <span className="text-xs italic text-muted-foreground">{def.partOfSpeech}</span>}
          {translation && <span className="text-sm font-medium text-sky-700 dark:text-sky-300">{translation}</span>}
        </p>
        {def ? (
          <>
            <p className="mt-1 text-sm text-foreground">{def.meaning}</p>
            {def.example && <p className="mt-1 text-sm italic text-muted-foreground">“{def.example}”</p>}
          </>
        ) : locked ? (
          <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
            <Lock className="h-3.5 w-3.5" aria-hidden /> {t('vocab.definition_locked')}
          </p>
        ) : (
          item.context && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{item.context}</p>
        )}
      </div>
      <Button type="button" variant="ghost" size="icon" onClick={onDelete} aria-label={t('vocab.delete', { word: item.word })}>
        <Trash2 className="h-4 w-4" aria-hidden />
      </Button>
    </li>
  );
}

/** "Mening so'zlarim": words saved from IELTS/SAT passages. Definitions and the download come with a plan. */
export default function PortalWords() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useVocabulary();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [busy, setBusy] = useState<'define' | 'download' | null>(null);
  // The view is in the URL, so a shared or reloaded link opens the same tab.
  const [params, setParams] = useSearchParams();
  const view: WordsView = params.get('view') === 'lists' ? 'lists' : 'mine';
  const savedWords = useMemo(() => new Set((data?.words ?? []).map((w) => w.word.toLowerCase())), [data]);

  const locked = data ? !data.access : false;
  const undefinedCount = data?.words.filter((w) => !w.definition).length ?? 0;

  const refresh = () => queryClient.invalidateQueries({ queryKey: VOCAB_QUERY_KEY });

  const run = async (kind: 'define' | 'download') => {
    if (locked) {
      setUpgradeOpen(true);
      return;
    }
    setBusy(kind);
    try {
      if (kind === 'define') {
        // Twenty words per call: keep going until every saved word has a definition.
        let remaining = undefinedCount;
        while (remaining > 0) {
          const result = await defineVocabWords();
          remaining = result.defined > 0 ? result.remaining : 0;
        }
        await refresh();
      } else {
        saveBlob(await downloadVocabCsv(), 'my-words.csv');
      }
    } catch (error) {
      if (vocabErrorCode(error) === 'PORTAL_PLAN_REQUIRED') setUpgradeOpen(true);
      else toast({ title: t(kind === 'define' ? 'vocab.define_failed' : 'vocab.download_failed'), variant: 'destructive' });
      await refresh();
    } finally {
      setBusy(null);
    }
  };

  const saveListWord = async (item: ListWord) => {
    try {
      await saveVocabWord(item.word, item.example);
      toast({ title: t('vocab.saved', { word: item.word }), variant: 'success' });
      await refresh();
    } catch {
      toast({ title: t('vocab.save_failed'), variant: 'destructive' });
    }
  };

  const remove = async (item: VocabWord) => {
    try {
      await deleteVocabWord(item.id);
      await refresh();
    } catch {
      toast({ title: t('vocab.delete_failed'), variant: 'destructive' });
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-5 px-4 py-6 md:px-6 md:py-8">
      <PortalHeading
        title={t('vocab.title')}
        description={t('vocab.subtitle')}
        aside={
          view === 'mine' && data?.words.length ? (
            <div className="flex shrink-0 gap-2">
              {undefinedCount > 0 && (
                <Button type="button" variant="outline" onClick={() => run('define')} disabled={busy !== null}>
                  {busy === 'define' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : locked ? <Lock className="mr-2 h-4 w-4" aria-hidden /> : <Sparkles className="mr-2 h-4 w-4" aria-hidden />}
                  {t('vocab.define', { count: undefinedCount })}
                </Button>
              )}
              <Button type="button" onClick={() => run('download')} disabled={busy !== null}>
                {busy === 'download' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : locked ? <Lock className="mr-2 h-4 w-4" aria-hidden /> : <Download className="mr-2 h-4 w-4" aria-hidden />}
                {t('vocab.download')}
              </Button>
            </div>
          ) : null
        }
      />

      <SegmentedTabs<WordsView>
        aria-label={t('vocab.title')}
        className="flex h-10 w-full self-start md:inline-flex md:w-auto [&>button]:flex-1 [&>button]:justify-center md:[&>button]:flex-none"
        options={[
          { value: 'mine' as const, label: t('wordLists.tab_mine') },
          { value: 'lists' as const, label: t('wordLists.tab_lists') },
        ]}
        value={view}
        onChange={(next) => setParams(next === 'lists' ? { view: 'lists' } : {}, { replace: true })}
      />

      {view === 'lists' ? (
        <WordLists savedWords={savedWords} onSave={saveListWord} />
      ) : (
      <>
      {locked && data && data.words.length > 0 && (
        <p className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">{t('vocab.plan_hint')}</p>
      )}

      <section className="overflow-hidden rounded-lg border border-border bg-card">
        {isLoading ? (
          <div className="space-y-3 p-5" aria-hidden="true">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ) : isError ? (
          <p className="p-5 text-sm text-destructive">{t('vocab.load_failed')}</p>
        ) : !data?.words.length ? (
          <EmptyState icon={<BookmarkPlus className="h-5 w-5" />} title={t('vocab.empty_title')} description={t('vocab.empty_body')} />
        ) : (
          <ul className="divide-y divide-border">
            {data.words.map((item) => (
              <WordRow key={item.id} item={item} locked={locked} onDelete={() => void remove(item)} />
            ))}
          </ul>
        )}
      </section>
      </>
      )}

      <PlanCheckoutModal open={upgradeOpen} onOpenChange={setUpgradeOpen} defaultPlan="PRO" />
    </div>
  );
}
